import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Divider,
  HStack,
  IconButton,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
  Tooltip,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { MdChevronRight, MdDelete, MdDownload, MdExpandMore } from 'react-icons/md';
import { toast } from 'react-toastify';

import Card from '@/components/ui/Card/Card';

import { useDeleteSettlementImportMutation } from '../api';
import { SettlementImportListItem } from '../types';
import { downloadSettlementReport } from '../utils/downloadSettlementReport';
import { formatAmount } from '../utils/money';

import { UnrecognizedPayments } from './UnrecognizedPayments';

interface SettlementImportItemProps {
  item: SettlementImportListItem;
}

export const SettlementImportItem: React.FC<SettlementImportItemProps> = ({ item }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onOpenDeleteModal,
    onClose: onCloseDeleteModal,
  } = useDisclosure();

  const [deleteImport, { isLoading: isDeleting }] = useDeleteSettlementImportMutation();
  const [isGeneratingSimple, setIsGeneratingSimple] = useState(false);
  const [isGeneratingDetailed, setIsGeneratingDetailed] = useState(false);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDelete = async () => {
    try {
      await deleteImport(item.id).unwrap();
      toast.success('Import został usunięty');
      onCloseDeleteModal();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Wystąpił błąd podczas usuwania');
    }
  };

  const handleGenerateSimpleReport = async () => {
    try {
      setIsGeneratingSimple(true);
      await downloadSettlementReport(item.id, item.fileName, false);
      toast.success('Raport uproszczony został wygenerowany');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Wystąpił błąd podczas generowania raportu');
    } finally {
      setIsGeneratingSimple(false);
    }
  };

  const handleGenerateDetailedReport = async () => {
    try {
      setIsGeneratingDetailed(true);
      await downloadSettlementReport(item.id, item.fileName, true);
      toast.success('Raport szczegółowy został wygenerowany');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Wystąpił błąd podczas generowania raportu');
    } finally {
      setIsGeneratingDetailed(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <Card>
        <Box color="black">
          <Stack spacing={4}>
            <Stack direction={{ base: 'column', xl: 'row' }} justify="space-between" align="start">
              <HStack align="center" spacing={3}>
                <IconButton
                  aria-label="Rozwiń"
                  icon={isExpanded ? <MdExpandMore /> : <MdChevronRight />}
                  size="sm"
                  variant="ghost"
                  onClick={handleToggleExpand}
                />
                <Box>
                  <Text fontWeight="semibold" overflowWrap="anywhere">
                    {item.fileName}
                  </Text>
                  <Text fontSize="sm">{item.format === 'sgb' ? 'SGB' : 'Warszawa'}</Text>
                  <HStack spacing={3} fontSize="sm" color="gray.600" flexWrap="wrap">
                    <Text>Zaimportowano: {formatDate(item.importDate)}</Text>
                    <Text>•</Text>
                    <Text>
                      {item.facilitiesCount}{' '}
                      {item.facilitiesCount === 1
                        ? 'placówka'
                        : item.facilitiesCount > 1 && item.facilitiesCount < 5
                        ? 'placówki'
                        : 'placówek'}
                    </Text>
                  </HStack>
                </Box>
              </HStack>
              <HStack spacing={4} flexWrap="wrap" rowGap={3}>
                <HStack spacing={3}>
                  <VStack align="end" spacing={0} whiteSpace="nowrap">
                    <Text fontSize="xs" color="gray.500">
                      Online
                    </Text>
                    <Text fontWeight="semibold" fontSize="sm" color="green.600">
                      {formatAmount(item.onlineTotal)}
                    </Text>
                  </VStack>
                  <VStack align="end" spacing={0} whiteSpace="nowrap">
                    <Text fontSize="xs" color="gray.500">
                      Przelewy
                    </Text>
                    <Text fontWeight="semibold" fontSize="sm" color="blue.600">
                      {formatAmount(item.totalAmount - item.onlineTotal)}
                    </Text>
                  </VStack>
                  <VStack align="end" spacing={0} whiteSpace="nowrap">
                    <Text fontSize="xs" color="gray.500">
                      Przypisane
                    </Text>
                    <Text fontWeight="bold" fontSize="lg" color="purple.700">
                      {formatAmount(item.totalAmount)}
                    </Text>
                  </VStack>
                </HStack>
                <Divider orientation="vertical" height="40px" />
                <VStack align="end" spacing={1}>
                  <Text fontSize="xs" color="gray.600" fontWeight="medium">
                    Generowanie raportów
                  </Text>
                  <HStack spacing={2}>
                    <Tooltip label="Raport bez szczegółów transakcji" placement="top">
                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="outline"
                        leftIcon={<MdDownload />}
                        onClick={handleGenerateSimpleReport}
                        isLoading={isGeneratingSimple}
                        isDisabled={isGeneratingDetailed}
                      >
                        Uproszczony
                      </Button>
                    </Tooltip>
                    <Tooltip label="Raport ze szczegółami transakcji" placement="top">
                      <Button
                        size="xs"
                        colorScheme="purple"
                        variant="solid"
                        leftIcon={<MdDownload />}
                        onClick={handleGenerateDetailedReport}
                        isLoading={isGeneratingDetailed}
                        isDisabled={isGeneratingSimple}
                      >
                        Szczegółowy
                      </Button>
                    </Tooltip>
                  </HStack>
                </VStack>
                <Divider orientation="vertical" height="40px" />
                <IconButton
                  aria-label="Usuń"
                  icon={<MdDelete />}
                  size="md"
                  colorScheme="gray"
                  variant="ghost"
                  onClick={onOpenDeleteModal}
                  isLoading={isDeleting}
                />
              </HStack>
            </Stack>

            {!!item.unrecognizedCount && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text>
                  Nierozpoznane wpłaty: {item.unrecognizedCount} —{' '}
                  {Object.entries(item.unrecognizedTotals || {})
                    .map(([currency, amount]) => formatAmount(amount, currency))
                    .join(', ')}
                  . Nie są wliczone do sum placówek.
                </Text>
              </Alert>
            )}
            <Collapse in={isExpanded} animateOpacity>
              {item.facilitiesCount > 0 && (
                <Box mt={4} overflowX="auto">
                  <Table size="sm">
                    <Thead>
                      <Tr>
                        <Th>Placówka</Th>
                        <Th isNumeric>Płatności online</Th>
                        <Th isNumeric>Płatności przelewem</Th>
                        <Th isNumeric>Kwota łączna</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(item.reportData).map(([facilityCode, facilityData]) => (
                        <Tr key={facilityCode}>
                          <Td>{facilityData.facilityName}</Td>
                          <Td isNumeric>{formatAmount(facilityData.onlineTotal)}</Td>
                          <Td isNumeric>
                            {formatAmount(facilityData.total - facilityData.onlineTotal)}
                          </Td>
                          <Td isNumeric>{formatAmount(facilityData.total)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
              <UnrecognizedPayments payments={item.unrecognizedTransactions || []} />
            </Collapse>
          </Stack>
        </Box>
      </Card>

      <Modal isOpen={isDeleteModalOpen} onClose={onCloseDeleteModal} size="xs">
        <ModalContent>
          <ModalHeader>Czy na pewno chcesz usunąć ten import?</ModalHeader>
          <ModalFooter>
            <Button colorScheme="gray" mr={3} onClick={onCloseDeleteModal}>
              Anuluj
            </Button>
            <Button colorScheme="red" onClick={handleDelete} isLoading={isDeleting}>
              Usuń
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
